import React, { useCallback, useMemo } from "react";

import {
  AddToCartButton as _AddToCartButton,
  type AddToCartButtonProps as _AddToCartButtonProps,
} from "@bared/ui";

import { useCartAdd } from "~/hooks/useCart";
import { useWishlistToggle } from "~/hooks/useWishlist";

import type { ProductVariant } from "./route";

import { useProduct } from "./useProduct";

export interface AddToCartButtonProps {
  /** Current selected product variant */
  selectedVariant: Pick<ProductVariant, "id" | "status"> | null;
  /** Array of attributes to add to the line item */
  attributes?: { key: string; value: string }[];
  /** Called when the "Email me when restocked" button is clicked  */
  onBackInStockOpen?: () => void;
  /** Show the wishlist button */
  showWishlistButton?: boolean;
  /** Optional size for button */
  size?: _AddToCartButtonProps["size"];
}

export function AddToCartButton({
  size,
  attributes,
  selectedVariant,
  onBackInStockOpen,
  showWishlistButton = true,
}: AddToCartButtonProps) {
  const { addToCart, isPending } = useCartAdd();

  const { shopifyProduct, googleAnalyticsItem, klaviyoItem } = useProduct();

  const { isInWishlist, toggleWishlist } = useWishlistToggle({
    klaviyoItem,
    googleAnalyticsItem,
    variantId: selectedVariant?.id,
    productHandle: shopifyProduct.handle,
  });

  const onAddToCart = useCallback(() => {
    if (!selectedVariant) return;

    if ("UNAVAILABLE" === selectedVariant.status) return;

    if (!!onBackInStockOpen && "NOTIFY" === selectedVariant.status) {
      onBackInStockOpen();
      return;
    }

    addToCart({
      attributes,
      quantity: 1,
      merchandiseId: selectedVariant.id,
    });
  }, [addToCart, attributes, selectedVariant, onBackInStockOpen]);

  const onWishlistToggle = useCallback(() => {
    toggleWishlist();
  }, [toggleWishlist]);

  const label = useMemo(() => {
    if (isPending) return "Adding…";

    if (!selectedVariant) return "Select a size";

    if ("UNAVAILABLE" === selectedVariant.status) return "Unavailable";

    if ("PRE_ORDER" === selectedVariant.status) return "Pre-Order Now";

    if ("NOTIFY" === selectedVariant.status) {
      return !!onBackInStockOpen ? "Email me when restocked" : "Unavailable";
    }

    return "Add To Cart";
  }, [isPending, selectedVariant, onBackInStockOpen]);

  const disabled =
    isPending || !selectedVariant || "UNAVAILABLE" === selectedVariant.status;

  return (
    <_AddToCartButton
      size={size}
      label={label}
      disabled={disabled}
      onAddToCart={onAddToCart}
      isInWishlist={isInWishlist}
      onWishlistToggle={showWishlistButton ? onWishlistToggle : undefined}
    />
  );
}
